import "./styles.css";
import { readSignupSession, saveSignupSession } from "./signupSession.js";
import { MAX_EMAIL_LENGTH, normalizeEmail } from "../shared/email.js";

declare global {
  interface Window {
    dataLayer?: Array<Record<string, string>>;
  }
}

type AnalyticsEvent =
  | "prelaunch_page_view"
  | "follow_reveal"
  | "signup_attempt"
  | "signup_success"
  | "signup_error";

const pageOpenedAt = performance.now();

function track(event: AnalyticsEvent): void {
  // Event names only: never send the email address or form contents.
  window.dataLayer?.push({ event });
  window.dispatchEvent(
    new CustomEvent("juggernaut:analytics", { detail: { event } })
  );
}

document.querySelector<HTMLDivElement>("#app")!.innerHTML = `
  <div class="page-shell">
    <header class="site-header" aria-label="TheJuggernaut.ai">
      <a class="wordmark" href="/" aria-label="TheJuggernaut.ai home">
        <span>TheJuggernaut</span><span class="wordmark-dot">.ai</span>
      </a>
      <span class="signal" aria-hidden="true"><i></i>Transmission 001</span>
    </header>

    <main class="hero">
      <div class="atmosphere atmosphere-one" aria-hidden="true"></div>
      <div class="atmosphere atmosphere-two" aria-hidden="true"></div>

      <section class="questions" aria-labelledby="page-title">
        <p class="eyebrow"><span>Technology</span><span>Business</span><span>Consumer</span></p>
        <h1 id="page-title" class="sr-only">Three questions about the future of AI</h1>

        <ol class="question-list">
          <li>
            <span class="question-number" aria-hidden="true">01</span>
            <p>What can <strong>$100K of intelligence</strong> accomplish?</p>
          </li>
          <li>
            <span class="question-number" aria-hidden="true">02</span>
            <p>What <strong>$1M problem</strong> can AI eliminate?</p>
          </li>
          <li>
            <span class="question-number" aria-hidden="true">03</span>
            <p>What would <strong>100 million people</strong> pay <strong>$10</strong> for?</p>
          </li>
        </ol>

        <div class="action-area">
          <button id="reveal-button" class="primary-button" type="button" aria-expanded="false" aria-controls="signup-panel">
            <span>Follow for more</span>
            <svg aria-hidden="true" viewBox="0 0 24 24"><path d="M5 12h14M13 6l6 6-6 6"/></svg>
          </button>

          <section id="signup-panel" class="signup-panel" aria-labelledby="signup-title" hidden>
            <div class="signup-intro">
              <p class="panel-kicker">The next transmission</p>
              <h2 id="signup-title">Follow as the vision unfolds.</h2>
              <p>Occasional updates. Only when there’s something worth revealing.</p>
            </div>

            <form id="signup-form" novalidate>
              <div class="email-row">
                <label for="email">Email address</label>
                <div class="input-action">
                  <input id="email" name="email" type="email" inputmode="email" autocomplete="email" maxlength="${MAX_EMAIL_LENGTH}" placeholder="you@example.com" aria-describedby="form-status" required />
                  <button type="submit">Keep me informed</button>
                </div>
              </div>

              <div class="bot-field" aria-hidden="true">
                <label for="website">Website</label>
                <input id="website" name="website" type="text" tabindex="-1" autocomplete="off" />
              </div>

              <label class="consent">
                <input id="consent" name="consent" type="checkbox" required />
                <span>I agree to receive occasional email updates from TheJuggernaut.ai. I can unsubscribe at any time.</span>
              </label>

              <p id="form-status" class="form-status" role="status" aria-live="polite"></p>
            </form>

            <p class="privacy-note">We collect only your email address and consent record. We don’t sell personal information.</p>
          </section>
        </div>
      </section>
    </main>

    <footer class="site-footer">
      <span>© ${new Date().getFullYear()} TheJuggernaut.ai</span>
      <span>Built at the edge of what’s possible.</span>
    </footer>
  </div>
`;

const revealButton = document.querySelector<HTMLButtonElement>("#reveal-button")!;
const signupPanel = document.querySelector<HTMLElement>("#signup-panel")!;
const signupForm = document.querySelector<HTMLFormElement>("#signup-form")!;
const emailInput = document.querySelector<HTMLInputElement>("#email")!;
const consentInput = document.querySelector<HTMLInputElement>("#consent")!;
const websiteInput = document.querySelector<HTMLInputElement>("#website")!;
const formStatus = document.querySelector<HTMLParagraphElement>("#form-status")!;
const submitButton = signupForm.querySelector<HTMLButtonElement>("button[type='submit']")!;

function revealSignup(options: { focus: boolean; trackReveal: boolean }): void {
  revealButton.setAttribute("aria-expanded", "true");
  revealButton.hidden = true;
  signupPanel.hidden = false;
  requestAnimationFrame(() => signupPanel.classList.add("is-visible"));

  if (options.focus) {
    emailInput.focus();
  }
  if (options.trackReveal) {
    track("follow_reveal");
  }
}

function showEmailError(message: string): void {
  formStatus.textContent = message;
  formStatus.className = "form-status is-error";
  emailInput.setAttribute("aria-invalid", "true");
  emailInput.focus();
}

function showSignupComplete(message: string): void {
  signupForm.reset();
  signupForm.querySelector(".email-row")?.remove();
  signupForm.querySelector(".consent")?.remove();
  formStatus.textContent = message;
  formStatus.className = "form-status is-success";
}

revealButton.addEventListener("click", () => {
  revealSignup({ focus: true, trackReveal: true });
});

emailInput.addEventListener("input", () => {
  emailInput.removeAttribute("aria-invalid");
});

signupForm.addEventListener("submit", async (event) => {
  event.preventDefault();
  formStatus.className = "form-status";
  emailInput.removeAttribute("aria-invalid");

  const normalizedEmail = normalizeEmail(emailInput.value);
  if (!normalizedEmail || !emailInput.validity.valid) {
    showEmailError("Enter a valid email address.");
    return;
  }

  if (!consentInput.checked) {
    formStatus.textContent = "Please confirm that you’d like to receive updates.";
    formStatus.classList.add("is-error");
    consentInput.focus();
    return;
  }

  track("signup_attempt");
  submitButton.disabled = true;
  submitButton.textContent = "Joining…";
  formStatus.textContent = "";

  try {
    const response = await fetch("/api/signup", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        email: normalizedEmail,
        consent: consentInput.checked,
        website: websiteInput.value,
        timeToSubmitMs: Math.round(performance.now() - pageOpenedAt)
      })
    });
    const result = (await response.json()) as {
      code?: string;
      message?: string;
    };

    if (!response.ok) {
      if (result.code === "invalid_email") {
        showEmailError(result.message ?? "Enter a valid email address.");
      }
      throw new Error(result.message ?? "Something went wrong. Please try again.");
    }

    saveSignupSession(localStorage, normalizedEmail);
    showSignupComplete(result.message ?? "You’re in. Watch your inbox.");
    track("signup_success");
  } catch (error) {
    if (emailInput.getAttribute("aria-invalid") !== "true") {
      formStatus.textContent =
        error instanceof Error
          ? error.message
          : "Something went wrong. Please try again.";
      formStatus.classList.add("is-error");
    }
    submitButton.disabled = false;
    submitButton.textContent = "Keep me informed";
    track("signup_error");
  }
});

const savedSession = readSignupSession(localStorage);
if (savedSession.state === "valid") {
  revealSignup({ focus: false, trackReveal: false });
  showSignupComplete("You’re already following. Watch your inbox for what comes next.");
} else if (savedSession.state === "invalid") {
  revealSignup({ focus: false, trackReveal: false });
  emailInput.value = savedSession.email.slice(0, MAX_EMAIL_LENGTH);
  showEmailError("Please correct your saved email address to keep following.");
}

track("prelaunch_page_view");
